import { useEffect, useRef, useState } from 'preact/hooks'
import { VoiceCall, type CallOptions, type CallState } from '../services/voiceCall'
import { friendlyToolName } from '../services/toolNames'
import { GeneratedContentPreview } from './GeneratedContentPreview'
import type { GeneratedContent } from '../types'
import { loadUserSettings, saveUserSettings } from '../services/api'

/**
 * A voice call with the persona, over the whole window (as in the PiPar app):
 * the persona's name and picture, what is happening (connecting, your turn,
 * you speaking, thinking or using a tool, the persona speaking), the last
 * words of each side, the microphone level, and mute / hang up.
 */
export function VoiceCallScreen({
  options,
  personaEmoji,
  personaImageUrl,
  onClose,
}: {
  options: CallOptions
  personaEmoji?: string
  personaImageUrl?: string
  /** After hanging up; the call's discussion id when the server saved it. */
  onClose: (discussionId?: string) => void
}) {
  const [state, setState] = useState<CallState>('connecting')
  // The conversation so far, newest last: the bottom bubble shows whose turn it was.
  const [lines, setLines] = useState<CallLine[]>([])
  const textsRef = useRef<HTMLDivElement | null>(null)
  const [tools, setTools] = useState<string[]>([])
  // An image or document a tool made: shown over the call, as in the chat.
  const [generated, setGenerated] = useState<GeneratedContent | null>(null)
  const [level, setLevel] = useState(0)
  const [speech, setSpeech] = useState(false)
  const [muted, setMuted] = useState(false)
  // Text bubbles on or off: in your web settings on the server (every browser),
  // and in this browser for an instant start before they load.
  const [showTexts, setShowTexts] = useState(() => {
    try {
      return localStorage.getItem(SHOW_TEXTS_KEY) !== 'false'
    } catch {
      return true
    }
  })
  const rememberLocally = (value: boolean) => {
    try {
      localStorage.setItem(SHOW_TEXTS_KEY, String(value))
    } catch {
      // not remembered here: the server setting still is
    }
  }
  useEffect(() => {
    loadUserSettings('web').then((settings) => {
      if (typeof settings.call_show_texts === 'boolean') {
        setShowTexts(settings.call_show_texts)
        rememberLocally(settings.call_show_texts)
      }
    }).catch(() => {})
  }, [])
  const toggleTexts = () => {
    const next = !showTexts
    setShowTexts(next)
    rememberLocally(next)
    void saveUserSettings({ call_show_texts: next }, 'web')
  }
  const [error, setError] = useState('')
  const [seconds, setSeconds] = useState(0)
  const call = useRef<VoiceCall | null>(null)

  useEffect(() => {
    const c = new VoiceCall(options, {
      onState: setState,
      // Partial words update the user's open bubble; the final text closes it.
      onUserText: (text, final) => setLines((prev) => {
        const last = prev[prev.length - 1]
        const open = last && last.role === 'user' && !last.final
        if (!text) return open ? prev.slice(0, -1) : prev // only noise
        if (open) return [...prev.slice(0, -1), { role: 'user', text, final }]
        return [...prev, { role: 'user', text, final }]
      }),
      onAiText: (text) => setLines((prev) => [...prev, { role: 'ai', text, final: true }]),
      onTool: setTools,
      onGeneratedContent: setGenerated,
      onLevel: (l, s) => {
        setLevel(l)
        setSpeech(s)
      },
      onError: setError,
      onEnded: (discussionId) => onClose(discussionId),
    })
    call.current = c
    void c.start()
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void c.hangUp()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('keydown', onKey)
      void c.hangUp()
    }
  }, [])

  // Keep the newest bubble in view.
  useEffect(() => {
    const el = textsRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lines, error])

  const toggleMute = () => {
    const next = !muted
    setMuted(next)
    call.current?.setMuted(next)
  }

  // As in the app: while a tool runs its name is the status, even over the
  // persona's "one moment", until the answer comes.
  const usingTools = tools.length > 0 && (state === 'thinking' || state === 'ai_speaking')
  const status = usingTools ? tools.map((t) => friendlyToolName(t, options.language)).join(', ') : (() => {
    switch (state) {
      case 'connecting':
        return 'Connecting…'
      case 'listening':
        return muted ? 'Mic muted' : 'Start speaking'
      case 'user_speaking':
        return 'Speaking'
      case 'recognizing':
        return 'Listening…'
      case 'thinking':
        return 'Thinking…'
      case 'ai_speaking':
        return `${options.personaName} is speaking`
      case 'ended':
        return 'Call ended'
    }
  })()

  const indicator =
    usingTools ? 'orbit'
      : state === 'user_speaking' ? 'pulse-green'
      : state === 'ai_speaking' ? 'pulse-orange'
        : state === 'listening' ? 'idle'
          : state === 'ended' ? 'none' : 'orbit'

  const minutes = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

  return (
    <div class="voice-call" role="dialog" aria-label={`Call with ${options.personaName}`}>
      <div class="voice-call__top">
        <div class="voice-call__name">{options.personaName}</div>
        <div class="voice-call__timer">{state === 'connecting' ? '' : minutes}</div>
      </div>

      <div class="voice-call__persona">
        {personaImageUrl
          ? <img src={personaImageUrl} alt={options.personaName} class="voice-call__picture" />
          : <div class="voice-call__picture voice-call__picture--emoji">{personaEmoji || '🙂'}</div>}
      </div>

      <div class="voice-call__status">
        <div class={`voice-call__indicator voice-call__indicator--${indicator}`}>
          {indicator === 'orbit' && <><span /><span /><span /></>}
        </div>
        <div class="voice-call__status-text">{status}</div>
      </div>

      <div class="voice-call__texts" ref={textsRef}>
        {showTexts && lines.map((line, i) => (
          <div key={i} class={`voice-call__line voice-call__line--${line.role}${line.final ? '' : ' voice-call__line--partial'}`}>
            {line.text}
          </div>
        ))}
        {error && <div class="voice-call__line voice-call__line--error">{error}</div>}
      </div>

      <div class="voice-call__level" aria-hidden="true">
        <div class="voice-call__level-bar"
             style={{ width: `${Math.round(level * 100)}%`, background: speech ? '#4caf50' : '#ff9800' }} />
      </div>

      <div class="voice-call__actions">
        <button class={`voice-call__button${muted ? ' voice-call__button--active' : ''}`}
                onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'} disabled={state === 'ended'}>
          {muted ? '🔇' : '🎤'}
        </button>
        <button class={`voice-call__button${showTexts ? ' voice-call__button--active' : ''}`}
                onClick={toggleTexts} title={showTexts ? 'Hide the text' : 'Show the text'}>
          💬
        </button>
        <button class="voice-call__button voice-call__button--hangup" onClick={() => void call.current?.hangUp()}
                title="Hang up" disabled={state === 'ended'}>
          {/* The app's call_end: white on the red button (the 📞 emoji is itself red). */}
          <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" aria-hidden="true">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.67-1.85.996.996 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
          </svg>
        </button>
      </div>
      {generated && <GeneratedContentPreview content={generated} onClose={() => setGenerated(null)} />}
    </div>
  )
}

const SHOW_TEXTS_KEY = 'pipar_call_show_texts'

interface CallLine {
  role: 'user' | 'ai'
  text: string
  /** False while the user is still speaking (partial recognition). */
  final: boolean
}
